import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Check, Diamond, Crown, Medal, Plane, Star, Shield, Briefcase, GraduationCap, Heart, Globe2, BookUser, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPackages } from "@/lib/paymentsApi";

const ICONS = { diamond: Diamond, crown: Crown, medal: Medal, plane: Plane, star: Star, shield: Shield, briefcase: Briefcase,
  graduation: GraduationCap, heart: Heart, globe: Globe2, passport: BookUser, home: Home };

const TX = {
  en: { strip: "Packages ready for your visa file", from: "from", view: "View packages", eyebrow: "Packages", title: "Travel-ready packages", sub: "Everything your application needs, with transparent prices.", more: "See all packages", quote: "On request" },
  pt: { strip: "Pacotes prontos para o seu processo de visto", from: "a partir de", view: "Ver pacotes", eyebrow: "Pacotes", title: "Pacotes prontos para viajar", sub: "Tudo o que o seu pedido precisa, com preços transparentes.", more: "Ver todos os pacotes", quote: "Sob consulta" },
  fr: { strip: "Des forfaits prêts pour votre dossier de visa", from: "à partir de", view: "Voir les forfaits", eyebrow: "Forfaits", title: "Des forfaits prêts pour le voyage", sub: "Tout ce dont votre demande a besoin, avec des prix transparents.", more: "Voir tous les forfaits", quote: "Sur demande" },
};

/** Home page: promo strip + up to 3 package cards, all managed in ERP > Settings > Website packages. */
export const HomePackages = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const tx = TX[lang] || TX.en;
  const tr = (v) => (v && (v[lang] || v.en || v.pt)) || "";
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { getPackages().then((d) => setPkgs(d.packages || [])).catch(() => {}); }, []);
  if (!pkgs.length) return null;
  const eurs = pkgs.map((p) => p.prices?.eur).filter((x) => x != null);
  const min = eurs.length ? Math.min(...eurs) : null;
  return (
    <>
      <section className="relative overflow-hidden border-b border-white/10 brand-gradient" data-testid="home-packages-strip">
        <div className="mx-auto max-w-7xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <p className="text-white text-sm sm:text-base font-medium">
            <span className="font-semibold">{tx.strip}</span>{min != null && <> — {tx.from} <span className="font-serif text-lg">€{min}</span></>}
          </p>
          <Button asChild size="sm" className="rounded-full bg-white text-venka-blue hover:bg-white/90 font-semibold shrink-0" data-testid="home-packages-cta">
            <Link to="/packages">{tx.view} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="home-packages">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-2">{tx.eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white">{tx.title}</h2>
            <p className="mt-2 text-slate-400 max-w-xl">{tx.sub}</p>
          </div>
          <Link to="/packages" className="inline-flex items-center gap-1.5 text-sm font-semibold text-venka-green-light hover:text-white">{tx.more} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {pkgs.slice(0, 3).map((p, i) => {
            const Icon = ICONS[p.icon] || Star;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to="/packages" className={`group block h-full rounded-3xl border p-7 transition-colors ${p.highlight ? "border-venka-green/40 bg-card shadow-2xl shadow-venka-blue/10" : "border-white/10 bg-card hover:border-white/25"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${p.highlight ? "brand-gradient text-white" : "bg-venka-blue/15 text-venka-green-light"}`}><Icon className="h-5 w-5" /></span>
                    {tr(p.badge) && <span className="rounded-full brand-gradient px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">{tr(p.badge)}</span>}
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-white">{tr(p.name)}</h3>
                  <p className="mt-1 text-sm text-slate-400 line-clamp-2">{tr(p.tagline)}</p>
                  <p className="mt-5 font-serif text-3xl text-white">{p.cta === "buy" && p.prices?.eur != null ? `€${p.prices.eur}` : tx.quote}</p>
                  <ul className="mt-5 space-y-2">
                    {(p.features || []).slice(0, 3).map((f, k) => <li key={k} className="flex items-start gap-2 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-venka-green-light" />{tr(f)}</li>)}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-venka-green-light group-hover:gap-2.5 transition-all">{tx.view} <ArrowRight className="h-4 w-4" /></span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default HomePackages;
